import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowRight, 
  GitBranch, 
  ShieldCheck, 
  MessageSquare, 
  HandshakeIcon,
  Building2,
  FileSearch,
  Bot,
  Code2,
  FileText,
  Users,
  Check,
  ChevronRight,
  ExternalLink,
  Play,
  Cpu,
  Loader2,
} from 'lucide-react';

// Full agent data - Step 7
const agentsFullData = {
  'pr-verification-agent': {
    name: 'PR Verification Agent',
    category: 'Engineering',
    icon: GitBranch,
    color: '#1A3A8F',
    overview: 'The PR Verification Agent analyzes pull requests for code quality, security vulnerabilities, and best practice compliance. It provides automated feedback to developers and helps maintain consistent code standards across your organization.',
    capabilities: [
      'Static code analysis for quality and security',
      'Best practice and style guide enforcement',
      'Automated test coverage verification',
      'Dependency vulnerability scanning',
      'Code complexity analysis',
      'Performance impact assessment',
    ],
    workflows: ['PR Governance', 'Security & Compliance'],
    integrations: [
      { name: 'GitHub', description: 'Full PR integration with status checks' },
      { name: 'GitLab', description: 'Merge request analysis and comments' },
      { name: 'Bitbucket', description: 'Pull request verification support' },
      { name: 'Jira', description: 'Issue linking and tracking' },
    ],
    exampleUsage: `
// Example: Configure PR Verification Agent
const config = {
  repository: "github.com/your-org/your-repo",
  rules: {
    minCoverage: 80,
    maxComplexity: 15,
    securityScan: true,
    styleGuide: "airbnb"
  },
  notifications: {
    slack: "#code-reviews",
    email: ["lead@company.com"]
  }
};

await liquicent.agents.deploy("pr-verification", config);
    `,
  },
  'security-audit-agent': {
    name: 'Security Audit Agent',
    category: 'Security',
    icon: ShieldCheck,
    color: '#2CB1A1',
    overview: 'The Security Audit Agent continuously scans code and infrastructure for security vulnerabilities and compliance issues. It integrates with your CI/CD pipeline to catch security issues before they reach production.',
    capabilities: [
      'Vulnerability detection (OWASP Top 10)',
      'Secret and credential scanning',
      'Dependency security audits',
      'Infrastructure as Code analysis',
      'Compliance requirement mapping',
      'Risk scoring and prioritization',
    ],
    workflows: ['Security & Compliance', 'PR Governance'],
    integrations: [
      { name: 'Snyk', description: 'Dependency vulnerability scanning' },
      { name: 'SonarQube', description: 'Code quality and security analysis' },
      { name: 'Veracode', description: 'Application security testing' },
      { name: 'AWS Security Hub', description: 'Cloud security posture' },
    ],
    exampleUsage: `
// Example: Security Audit Agent configuration
const securityConfig = {
  scanTypes: ["sast", "dast", "sca"],
  compliance: ["SOC2", "HIPAA", "PCI-DSS"],
  thresholds: {
    critical: 0,
    high: 5,
    medium: 20
  },
  autoRemediate: true
};

await liquicent.agents.deploy("security-audit", securityConfig);
    `,
  },
  'ticket-classification-agent': {
    name: 'Ticket Classification Agent',
    category: 'Support',
    icon: MessageSquare,
    color: '#1A3A8F',
    overview: 'The Ticket Classification Agent automatically categorizes and prioritizes support tickets for optimal routing. It uses natural language understanding to identify ticket intent, urgency, and required expertise.',
    capabilities: [
      'Intent classification (30+ categories)',
      'Urgency and priority detection',
      'Sentiment analysis',
      'Customer tier identification',
      'Auto-routing to specialized teams',
      'Duplicate ticket detection',
    ],
    workflows: ['Customer Support'],
    integrations: [
      { name: 'Zendesk', description: 'Ticket classification and routing' },
      { name: 'Intercom', description: 'Conversation categorization' },
      { name: 'Freshdesk', description: 'Support ticket automation' },
      { name: 'Salesforce Service Cloud', description: 'Case management' },
    ],
    exampleUsage: `
// Example: Ticket Classification setup
const supportConfig = {
  categories: ["billing", "technical", "feature_request", "bug"],
  priorities: {
    enterprise: "high",
    standard: "normal",
    trial: "low"
  },
  routing: {
    billing: "finance-team",
    technical: "engineering-support"
  }
};

await liquicent.agents.deploy("ticket-classification", supportConfig);
    `,
  },
  'deal-validation-agent': {
    name: 'Deal Validation Agent',
    category: 'Sales',
    icon: HandshakeIcon,
    color: '#2CB1A1',
    overview: 'The Deal Validation Agent reviews deal terms against company policies and pricing guidelines. It accelerates deal desk operations while ensuring consistent governance.',
    capabilities: [
      'Pricing policy compliance check',
      'Discount threshold validation',
      'Contract term analysis',
      'Deal structure optimization',
      'Approval workflow routing',
      'Historical deal comparison',
    ],
    workflows: ['Sales Deal Desk'],
    integrations: [
      { name: 'Salesforce', description: 'Opportunity and quote validation' },
      { name: 'HubSpot', description: 'Deal pipeline integration' },
      { name: 'Pipedrive', description: 'Sales automation' },
      { name: 'DocuSign', description: 'Contract analysis' },
    ],
    exampleUsage: `
// Example: Deal Validation configuration
const dealConfig = {
  maxDiscount: {
    smb: 15,
    midMarket: 25,
    enterprise: 40
  },
  requiredApprovals: {
    above100k: ["sales-director", "finance"],
    above500k: ["vp-sales", "cfo"]
  }
};

await liquicent.agents.deploy("deal-validation", dealConfig);
    `,
  },
  'vendor-risk-agent': {
    name: 'Vendor Risk Agent',
    category: 'Compliance',
    icon: Building2,
    color: '#1A3A8F',
    overview: 'The Vendor Risk Agent assesses vendor financial and operational risk for onboarding and ongoing monitoring. It automates due diligence and provides continuous risk scoring.',
    capabilities: [
      'Financial health assessment',
      'Operational risk scoring',
      'Compliance verification',
      'Third-party data enrichment',
      'Continuous monitoring alerts',
      'Risk report generation',
    ],
    workflows: ['Vendor Onboarding'],
    integrations: [
      { name: 'SAP Ariba', description: 'Supplier management' },
      { name: 'Coupa', description: 'Procurement platform' },
      { name: 'Workday', description: 'Vendor master data' },
      { name: 'Dun & Bradstreet', description: 'Business data enrichment' },
    ],
    exampleUsage: `
// Example: Vendor Risk Agent setup
const vendorConfig = {
  riskFactors: ["financial", "operational", "compliance", "security"],
  thresholds: {
    autoApprove: { score: 85, value: 50000 },
    escalate: { score: 60 }
  },
  monitoring: {
    frequency: "weekly",
    alerts: ["risk-score-change", "news-events"]
  }
};

await liquicent.agents.deploy("vendor-risk", vendorConfig);
    `,
  },
  'reviewer-routing-agent': {
    name: 'Reviewer Routing Agent',
    category: 'Engineering',
    icon: Users,
    color: '#2CB1A1',
    overview: 'The Reviewer Routing Agent assigns optimal code reviewers based on expertise, workload, and availability. It learns from historical review patterns to improve assignments.',
    capabilities: [
      'Expertise matching based on code ownership',
      'Workload balancing across team',
      'Availability and timezone awareness',
      'Review quality scoring',
      'Load distribution optimization',
      'Knowledge sharing promotion',
    ],
    workflows: ['PR Governance'],
    integrations: [
      { name: 'GitHub', description: 'CODEOWNERS and reviewer assignment' },
      { name: 'GitLab', description: 'Merge request reviewers' },
      { name: 'Jira', description: 'Developer workload data' },
      { name: 'Slack', description: 'Availability status' },
    ],
    exampleUsage: `
// Example: Reviewer Routing configuration
const routingConfig = {
  strategy: "balanced", // or "expertise-first", "round-robin"
  maxReviewsPerDev: 5,
  excludeTimezones: ["off-hours"],
  requiredReviewers: 2,
  expertiseWeight: 0.6
};

await liquicent.agents.deploy("reviewer-routing", routingConfig);
    `,
  },
  'knowledge-retrieval-agent': {
    name: 'Knowledge Retrieval Agent',
    category: 'Support',
    icon: FileSearch,
    color: '#1A3A8F',
    overview: 'The Knowledge Retrieval Agent searches knowledge bases to find relevant solutions for support issues. It uses semantic search to surface the most helpful content.',
    capabilities: [
      'Semantic search across knowledge bases',
      'Multi-source aggregation',
      'Relevance ranking',
      'Answer extraction and summarization',
      'Knowledge gap identification',
      'Content freshness monitoring',
    ],
    workflows: ['Customer Support'],
    integrations: [
      { name: 'Confluence', description: 'Wiki and documentation search' },
      { name: 'Notion', description: 'Workspace knowledge retrieval' },
      { name: 'SharePoint', description: 'Enterprise content search' },
      { name: 'Zendesk Guide', description: 'Help center articles' },
    ],
    exampleUsage: `
// Example: Knowledge Retrieval setup
const knowledgeConfig = {
  sources: [
    { type: "confluence", space: "SUPPORT" },
    { type: "notion", database: "kb-articles" }
  ],
  searchSettings: {
    semanticSearch: true,
    minRelevance: 0.7,
    maxResults: 5
  }
};

await liquicent.agents.deploy("knowledge-retrieval", knowledgeConfig);
    `,
  },
  'compliance-policy-agent': {
    name: 'Compliance Policy Agent',
    category: 'Compliance',
    icon: FileText,
    color: '#2CB1A1',
    overview: 'The Compliance Policy Agent enforces coding standards, security policies, and regulatory requirements. It provides automated policy enforcement with clear feedback.',
    capabilities: [
      'Custom policy definition',
      'Real-time policy enforcement',
      'Violation detection and reporting',
      'Exemption management',
      'Audit trail generation',
      'Policy version control',
    ],
    workflows: ['PR Governance', 'Security & Compliance'],
    integrations: [
      { name: 'Open Policy Agent', description: 'Policy as code' },
      { name: 'HashiCorp Sentinel', description: 'Policy enforcement' },
      { name: 'Custom Policies', description: 'Organization-specific rules' },
    ],
    exampleUsage: `
// Example: Compliance Policy setup
const policyConfig = {
  policies: [
    { name: "no-secrets-in-code", severity: "critical" },
    { name: "require-tests", severity: "high" },
    { name: "max-file-size", limit: "1MB", severity: "medium" }
  ],
  exemptions: {
    teams: ["platform-team"],
    paths: ["/legacy/*"]
  }
};

await liquicent.agents.deploy("compliance-policy", policyConfig);
    `,
  },
  'contract-review-agent': {
    name: 'Contract Review Agent',
    category: 'Legal',
    icon: FileText,
    color: '#1A3A8F',
    overview: 'The Contract Review Agent analyzes contract terms to identify risks and non-standard clauses. Built by LegalTech Inc. for the Liquicent community.',
    capabilities: [
      'Clause extraction and classification',
      'Risk identification',
      'Non-standard term detection',
      'Comparison to standard templates',
      'Negotiation recommendations',
      'Obligation tracking',
    ],
    workflows: ['Vendor Onboarding', 'Sales Deal Desk'],
    integrations: [
      { name: 'DocuSign', description: 'Contract lifecycle management' },
      { name: 'Ironclad', description: 'Contract automation' },
      { name: 'Agiloft', description: 'CLM integration' },
    ],
    exampleUsage: `
// Example: Contract Review configuration
const contractConfig = {
  templates: ["master-service-agreement", "nda", "sow"],
  riskCategories: ["liability", "indemnification", "termination"],
  flagThreshold: "medium",
  autoSummarize: true
};

await liquicent.agents.deploy("contract-review", contractConfig);
    `,
    author: 'LegalTech Inc.',
  },
  'expense-validation-agent': {
    name: 'Expense Validation Agent',
    category: 'Finance',
    icon: Code2,
    color: '#2CB1A1',
    overview: 'The Expense Validation Agent reviews expense reports for policy compliance and fraud detection. Built by FinOps Labs for the Liquicent community.',
    capabilities: [
      'Receipt verification',
      'Policy compliance checking',
      'Fraud pattern detection',
      'Duplicate expense identification',
      'Category suggestion',
      'Auto-approval for qualifying expenses',
    ],
    workflows: ['Expense Management'],
    integrations: [
      { name: 'Expensify', description: 'Expense report integration' },
      { name: 'Concur', description: 'SAP expense management' },
      { name: 'Brex', description: 'Corporate card integration' },
    ],
    exampleUsage: `
// Example: Expense Validation setup
const expenseConfig = {
  policies: {
    meals: { limit: 75, requireReceipt: true },
    travel: { limit: 500, requireApproval: "manager" }
  },
  autoApprove: {
    maxAmount: 50,
    categories: ["office-supplies", "software"]
  }
};

await liquicent.agents.deploy("expense-validation", expenseConfig);
    `,
    author: 'FinOps Labs',
  },
};

// Try Agent Sandbox Component
const TryAgentSandbox = ({ agent }) => {
  const [input, setInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);

  const sampleInputs = {
    'pr-verification-agent': 'https://github.com/example/repo/pull/123',
    'security-audit-agent': 'Scan repository: github.com/company/app',
    'ticket-classification-agent': 'Customer complaint: Unable to login to dashboard after password reset',
    'deal-validation-agent': 'Deal: Acme Corp, $150,000, 25% discount, 2-year term',
    'vendor-risk-agent': 'Vendor: TechSupplier Inc., Location: US, Industry: Software',
    'reviewer-routing-agent': 'PR: #456, Files: api/users.py, tests/test_users.py',
    'knowledge-retrieval-agent': 'How do I reset my password?',
    'compliance-policy-agent': 'Check file: config/secrets.json',
    'contract-review-agent': 'Contract term: Unlimited liability clause detected',
    'expense-validation-agent': 'Expense: $120 dinner at Restaurant XYZ, 4 attendees',
  };

  const sampleResults = {
    'pr-verification-agent': {
      status: 'PASSED',
      score: 87,
      findings: [
        { type: 'info', message: 'Test coverage: 92%' },
        { type: 'warning', message: 'Complexity slightly high in api/utils.py' },
        { type: 'success', message: 'No security vulnerabilities detected' },
      ],
      recommendation: 'Approve with minor suggestions',
    },
    'security-audit-agent': {
      status: 'REVIEW_REQUIRED',
      score: 72,
      findings: [
        { type: 'critical', message: '1 hardcoded credential detected' },
        { type: 'warning', message: '3 outdated dependencies' },
        { type: 'success', message: 'OWASP Top 10 compliant' },
      ],
      recommendation: 'Address critical finding before deployment',
    },
    'ticket-classification-agent': {
      category: 'Technical Support',
      priority: 'High',
      sentiment: 'Frustrated',
      suggestedTeam: 'Authentication Team',
      suggestedArticles: ['Password Reset Guide', 'Login Troubleshooting'],
    },
    'deal-validation-agent': {
      status: 'APPROVAL_REQUIRED',
      discountCompliance: 'Within policy (max 30%)',
      riskScore: 'Medium',
      requiredApprovers: ['Sales Director', 'Finance'],
      recommendation: 'Route to approval workflow',
    },
    'vendor-risk-agent': {
      riskScore: 78,
      riskLevel: 'Low',
      financialHealth: 'Strong',
      complianceStatus: 'Verified',
      recommendation: 'Proceed with onboarding',
    },
  };

  const runAgent = () => {
    if (isRunning || !input.trim()) return;
    
    setIsRunning(true);
    setResult(null);
    
    // Simulate agent execution
    setTimeout(() => {
      const agentKey = Object.keys(sampleResults).find(key => 
        agent.name.toLowerCase().includes(key.replace('-agent', '').replace(/-/g, ' '))
      );
      setResult(sampleResults[agentKey] || sampleResults['pr-verification-agent']);
      setIsRunning(false);
    }, 2000);
  };

  const agentKey = Object.keys(sampleInputs).find(key => 
    agent.name.toLowerCase().includes(key.replace('-agent', '').replace(/-/g, ' '))
  );
  const placeholder = sampleInputs[agentKey] || 'Enter your input...';

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#0F172A]">Try Agent</h2>
        <span className="text-xs font-medium px-3 py-1 bg-[#2CB1A1]/10 text-[#2CB1A1] rounded-full">
          Interactive Demo
        </span>
      </div>
      
      <Card 
        className="p-6 bg-white border-2 border-[#2CB1A1]/30 rounded-xl"
        style={{ boxShadow: '0px 6px 16px rgba(44, 177, 161, 0.1)' }}
      >
        <div className="space-y-4">
          {/* Input */}
          <div>
            <label className="text-sm font-medium text-[#0F172A] mb-2 block">Input</label>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              rows={3}
              className="bg-[#F6F7FB] border-[#E5E7EB] focus:border-[#2CB1A1] resize-none"
            />
            <button 
              onClick={() => setInput(placeholder)}
              className="text-xs text-[#2CB1A1] mt-2 hover:underline"
            >
              Use sample input
            </button>
          </div>

          {/* Run Button */}
          <Button
            onClick={runAgent}
            disabled={isRunning || !input.trim()}
            className="w-full bg-[#2CB1A1] hover:bg-[#239688] text-white"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Agent...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run {agent.name}
              </>
            )}
          </Button>

          {/* Result */}
          {result && (
            <div className="mt-4 p-4 bg-[#F6F7FB] rounded-lg border border-[#E5E7EB]">
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-4 h-4 text-[#2CB1A1]" />
                <span className="text-sm font-semibold text-[#0F172A]">Agent Output</span>
              </div>
              <pre className="text-xs font-mono text-[#475569] whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
              <p className="text-xs text-[#64748B] mt-3 pt-3 border-t border-[#E5E7EB]">
                This is a demo. In production, results are recorded to the Unified Transaction Graph.
              </p>
            </div>
          )}
        </div>
      </Card>
    </section>
  );
};

const AgentDetailNew = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const agent = agentsFullData[agentId];

  if (!agent) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Agent not found</h1>
          <Link to="/agents" className="text-[#1A3A8F] hover:underline">Return to Agent Registry</Link>
        </div>
      </div>
    );
  }

  const AgentIcon = agent.icon;

  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>{agent.name} | Liquicent Agents</title>
        <meta name="description" content={agent.overview} />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6">
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${agent.color}10` }}
              >
                <AgentIcon className="w-10 h-10" style={{ color: agent.color }} />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium px-3 py-1 bg-[#F6F7FB] rounded-full text-[#64748B]">
                    {agent.category}
                  </span>
                  {!agent.author && (
                    <span className="text-xs font-medium px-3 py-1 bg-[#1A3A8F]/10 text-[#1A3A8F] rounded-full">
                      Powered by Newton Engine
                    </span>
                  )}
                  {agent.author && (
                    <span className="text-xs font-medium px-3 py-1 bg-[#2CB1A1]/10 text-[#2CB1A1] rounded-full">
                      Community • by {agent.author}
                    </span>
                  )}
                </div>
                <h1 className="text-3xl lg:text-4xl font-bold text-[#0F172A]">
                  {agent.name}
                </h1>
              </div>
            </div>
            <div className="hidden lg:flex gap-3">
              <Button variant="outline" className="border-[#1A3A8F] text-[#1A3A8F]">
                Try Agent
              </Button>
              <Button className="bg-[#1A3A8F] hover:bg-[#142D70] text-white">
                Deploy Agent
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview */}
            <section>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Overview</h2>
              <p className="text-[#475569] leading-relaxed">{agent.overview}</p>
            </section>

            {/* Capabilities */}
            <section>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Capabilities</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {agent.capabilities.map((cap, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#2CB1A1]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#2CB1A1]" />
                    </div>
                    <span className="text-[#475569]">{cap}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Example Usage */}
            <section>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Example Usage</h2>
              <Card className="p-6 bg-[#0F172A] rounded-xl overflow-x-auto">
                <pre className="text-sm text-[#E2E8F0] font-mono whitespace-pre-wrap">
                  {agent.exampleUsage.trim()}
                </pre>
              </Card>
            </section>

            {/* Try Agent Sandbox */}
            <TryAgentSandbox agent={agent} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Workflows */}
            <Card className="p-6 bg-white border border-[#E5E7EB] rounded-xl">
              <h3 className="font-semibold text-[#0F172A] mb-4">Supported Workflows</h3>
              <div className="space-y-2">
                {agent.workflows.map((wf) => (
                  <Link 
                    key={wf}
                    to={`/workflows/${wf.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}`}
                    className="flex items-center justify-between p-3 bg-[#F6F7FB] rounded-lg hover:bg-[#1A3A8F]/5 transition-colors group"
                  >
                    <span className="text-sm text-[#475569] group-hover:text-[#1A3A8F]">{wf}</span>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#1A3A8F]" />
                  </Link>
                ))}
              </div>
            </Card>

            {/* Integrations */}
            <Card className="p-6 bg-white border border-[#E5E7EB] rounded-xl">
              <h3 className="font-semibold text-[#0F172A] mb-4">Integrations</h3>
              <div className="space-y-3">
                {agent.integrations.map((int) => (
                  <div key={int.name} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F6F7FB] flex items-center justify-center flex-shrink-0">
                      <ExternalLink className="w-4 h-4 text-[#64748B]" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-[#0F172A]">{int.name}</p>
                      <p className="text-xs text-[#64748B]">{int.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* CTA Card */}
            <Card className="p-6 bg-gradient-to-br from-[#1A3A8F] to-[#142D70] text-white rounded-xl">
              <h3 className="font-semibold mb-2">Ready to Deploy?</h3>
              <p className="text-sm text-white/80 mb-4">
                Get started with {agent.name} in your workflows.
              </p>
              <Button 
                onClick={() => navigate('/request-demo')}
                className="w-full bg-white text-[#1A3A8F] hover:bg-white/90"
              >
                Request Demo
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailNew;
